'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ShoppingCart } from 'lucide-react';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';
import { fmtMoney } from '@/lib/format';

/**
 * MONEY OWED FOLLOWS YOU DOWN THE PAGE
 * ====================================
 *
 * An unpaid seat is the only thing on this page with a clock on it — MP holds
 * it for about three hours — and it was sitting in the aside above the
 * household, pushing the list down at exactly the moment somebody was trying
 * to finish. So when there is something to pay, it leaves the flow and docks:
 * a bar across the bottom on a phone, a card in the corner on a desktop, in
 * both cases still there once the agenda has been read.
 *
 * ONE SURFACE THAT MORPHS, the way the platform's add buttons do (MorphMenu):
 * closed it is a circle with a cart in it, and opening grows that same element
 * into the card rather than swapping one element for another. Two things fall
 * out of that. The open size has to be measured, so the content stays mounted
 * at its natural width and the surface animates to it. And each direction
 * carries its own easing and duration, because the transition that runs is the
 * one declared on the state being animated TO: opening overshoots for the
 * give, closing must not, or the surface collapses past the circle before
 * settling back into it.
 *
 * ONE STACK, HOWEVER MANY INVOICES. A household can hold two unfinished
 * checkouts, and two independently fixed cards would sit on top of each
 * other. `PayDockHost` is the column; each card portals into it.
 *
 * FIXED, FROM INSIDE A WIDGET. On woodsidebible.org the widget lives in a
 * couple of `position: relative; z-index: 10` wrappers, so a fixed element
 * rendered in place is trapped in that stacking context and the theme's
 * header paints over it. The shell hangs a zero-size host off <body> for
 * exactly this, and the sheets already use it; with no host (the dev app)
 * `position: fixed` behaves and this renders where it stands.
 */

/**
 * The column element itself, held in state rather than a ref: a ref set
 * during mount does not re-render the cards that need it, and they would
 * portal into nothing on the only render that matters.
 */
const StackContext = createContext<HTMLElement | null | undefined>(undefined);

/**
 * The column docked cards render into, or `undefined` outside a host.
 *
 * Exported because the cart is not the only thing that docks: on a phone the
 * register bar lives in the same column, so the two stack rather than sitting
 * on top of each other. `null` means the host is mounted but its column has
 * not attached yet; `undefined` means there is no host at all and a card
 * should stay where it was rendered.
 */
export function useDockStack(): HTMLElement | null | undefined {
  return useContext(StackContext);
}

/**
 * A way to ask the dock to open itself from somewhere else on the page.
 *
 * Somebody's row in the who-list and the cart in the corner are the same
 * registration, so tapping the row should open the thing already on screen
 * rather than a second sheet saying the same numbers. The counter is what
 * makes a second tap work after the dock has been put away: the id alone
 * would be unchanged and the effect would not run again.
 */
const OpenContext = createContext<{
  requested: { id: string; n: number } | null;
  request: (id: string) => void;
} | null>(null);

/** Ask the docked card for this invoice to open. */
export function useDockRequest(): ((id: string) => void) | null {
  return useContext(OpenContext)?.request ?? null;
}

/** The closed circle, in px. */
const CIRCLE = 56;

/**
 * The closed corner, as a NUMBER rather than `rounded-full`.
 *
 * `rounded-full` is `9999px`, which a browser clamps to half the box — a
 * circle while the box is 56px, and a number the size of a house the moment it
 * is not. Animating that to the card's 20px means the radius plummets from
 * 9999 while the box is still growing, and for a few frames you get a big
 * panel with corners that look sharp, which then round themselves off after it
 * has finished opening. Half the circle is the same shape closed and a value
 * that interpolates to 20px without ever passing through pointy.
 */
const CLOSED_RADIUS = CIRCLE / 2;

/**
 * The column the docked cards live in. Mounted once by the page, whether or
 * not anything is owed — a container that appears at the same moment as its
 * first child has nowhere to put it.
 */
export function PayDockHost({ children }: { children?: ReactNode }) {
  const host = usePortalContainer();
  const [stack, setStack] = useState<HTMLElement | null>(null);
  const [requested, setRequested] = useState<{ id: string; n: number } | null>(null);
  const opener = useMemo(
    () => ({
      requested,
      request: (id: string) => setRequested((was) => ({ id, n: (was?.n ?? 0) + 1 })),
    }),
    [requested]
  );

  const column = (
    <div
      /* Bottom right on a desktop, where a cart is: the corner a hand
         already goes to, and the same side as the registration card it
         belongs to. Full width on a phone, where it is a bar.
         
         NO `items-start` ON THE PHONE. It sized this column to its own
         content, so a card asking for `w-full` got the content's width back
         and could grow wider than the screen — which is how the register
         bar's button ended up off the right edge. Stretching is what makes
         "full width" mean the window. The cart is unaffected either way: it
         sets its own pixel size and anchors itself. */
      className="ed pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col gap-2 px-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:items-end sm:px-0"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div ref={setStack} className="flex flex-col items-start gap-2 sm:items-end" />
    </div>
  );

  return (
    <OpenContext.Provider value={opener}>
      <StackContext.Provider value={stack}>
        {host ? createPortal(column, host) : column}
        {children}
      </StackContext.Provider>
    </OpenContext.Provider>
  );
}

export function PayDock({
  amount,
  id,
  title = 'Your cart',
  children,
}: {
  amount: number;
  /** The invoice this card is, so a row elsewhere can ask it to open. */
  id?: string;
  /** The band's label. The band is also how it is put away. */
  title?: string;
  children: ReactNode;
}) {
  const stack = useContext(StackContext);
  const opener = useContext(OpenContext);
  const [open, setOpen] = useState(true);

  // Somebody tapped their own row upstairs. This is their invoice.
  const asked = opener?.requested;
  useEffect(() => {
    if (asked && id && asked.id === id) setOpen(true);
  }, [asked, id]);
  const content = useRef<HTMLDivElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  // A tap anywhere else puts it away. It is an overlay over somebody's
  // reading, and the way back is a button with the number on it.
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const el = surface.current;
      if (el && !e.composedPath().includes(el)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  // The card stays mounted at its own width whether the surface is showing it
  // or not, so its size is known before the surface has to animate to it.
  useEffect(() => {
    const el = content.current;
    if (!el) return;
    const measure = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dims = open && size ? { width: size.w, height: size.h } : { width: CIRCLE, height: CIRCLE };

  const node = (
    // The wrapper holds the column's layout space; the surface morphs inside
    // it, anchored to the bottom left so the card grows up and out of the
    // circle rather than pushing it around.
    <div
      className="pointer-events-auto relative shrink-0 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
      style={dims}
    >
      <div
        ref={surface}
        style={{
          ...dims,
          borderRadius: open ? 'var(--radius-card)' : `${CLOSED_RADIUS}px`,
        }}
        className={`absolute bottom-0 left-0 overflow-hidden shadow-lg transition-all sm:right-0 sm:left-auto ${
          open
            ? 'bg-paper border-line border duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]'
            : 'accent-band duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]'
        }`}
      >
        {/* The cart is the surface in both states: the whole button when
            closed, the header band when open. The band is the way back out,
            and it says so — a chevron, a hover, and the whole row clickable,
            rather than an icon somebody has to guess at. */}
        <button
          type="button"
          aria-label={open ? 'Put this away' : `${fmtMoney(amount)} still to pay`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{ borderRadius: open ? 0 : CLOSED_RADIUS }}
          className={`accent-band absolute top-0 left-0 z-10 flex items-center transition-all ${
            open ? 'h-11 w-full gap-2 px-4 hover:brightness-95' : 'h-full w-full justify-center'
          }`}
        >
          <ShoppingCart className={open ? 'h-4 w-4 shrink-0' : 'h-5 w-5'} />
          {open && (
            <>
              <span className="text-sm font-bold">{title}</span>
              <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-80" />
            </>
          )}
        </button>
        <div
          ref={content}
          className={`w-[min(22rem,calc(100vw-1.5rem))] transition-opacity ${
            open ? 'opacity-100 delay-100 duration-200' : 'opacity-0 duration-100'
          }`}
        >
          {/* The band's own height, so the card's content starts under it. */}
          <div className="h-11" aria-hidden />
          {children}
        </div>
      </div>
      {!open && (
        <span className="bg-navy pointer-events-none absolute -top-1 -right-2 z-20 rounded-full px-1.5 py-0.5 text-[0.65rem] font-bold text-white shadow">
          {fmtMoney(amount)}
        </span>
      )}
    </div>
  );

  // Outside a host — the standalone pay page mounts this card by itself —
  // there is nothing to dock to, so it stays where it was rendered.
  if (stack === undefined) return <>{children}</>;
  if (!stack) return null;
  return createPortal(node, stack);
}
